import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import MatterDocumentsPanel from '../../components/MatterDocumentsPanel';
import DocumentGenerationPanel from '../../components/DocumentGenerationPanel';
import ProfessionalLearningInsights from '../../components/education/ProfessionalLearningInsights';
import ProfessionalAssignLearning from '../../components/education/ProfessionalAssignLearning';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const formatSupabaseError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const getStatusLabel = (status) => {
  if (status === 'matter_created') return 'Plan Created';
  if (status === 'ready_for_attorney_review') return 'Ready for Attorney Review';
  if (status === 'attorney_review') return 'Attorney Review In Progress';
  if (status === 'more_information_needed') return 'Waiting for Additional Information';
  if (status === 'attorney_review_complete') return 'Attorney Review Complete';
  if (status === 'ready_for_document_generation') return 'Ready for Document Generation';
  if (status === 'documents_generated') return 'Documents Generated';
  return status?.replace('matter', 'plan').replaceAll('_', ' ');
};

const getStatusBadge = (status) => {
  const base = 'px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize';

  switch (status) {
    case 'completed':
      return <span className={`${base} bg-green-100 text-green-800 border-green-200`}>Completed</span>;
    case 'intake_submitted':
    case 'legal_triage':
    case 'ready_for_attorney_review':
    case 'attorney_review':
    case 'more_information_needed':
      return <span className={`${base} bg-amber-50 text-amber-700 border-amber-100`}>{getStatusLabel(status)}</span>;
    case 'attorney_review_complete':
    case 'ready_for_document_generation':
    case 'documents_generated':
      return <span className={`${base} bg-emerald-50 text-emerald-700 border-emerald-100`}>{getStatusLabel(status)}</span>;
    default:
      return <span className={`${base} bg-blue-50 text-blue-700 border-blue-100`}>{getStatusLabel(status)}</span>;
  }
};

const getTaskStatusLabel = (status) => status?.replaceAll('_', ' ');

const stringifyValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not provided';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Not provided';
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return Object.values(item).filter(Boolean).join(' - ');
        }
        return String(item);
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const WORKFLOW_STATUSES = [
  { value: 'ready_for_attorney_review', label: 'Ready for Attorney Review' },
  { value: 'legal_triage', label: 'Legal Triage' },
  { value: 'more_information_needed', label: 'Waiting for Additional Information' },
  { value: 'attorney_review', label: 'Attorney Review In Progress' },
  { value: 'drafting', label: 'Drafting' },
  { value: 'drafts_ready_for_review', label: 'Drafts Ready For Review' },
  { value: 'revision_requested', label: 'Revision Requested' },
  { value: 'final_documents_ready', label: 'Final Documents Ready' },
  { value: 'execution_pending', label: 'Execution Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'funding_follow_up', label: 'Funding Follow Up' }
];

const LawFirmMatterDetail = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [intakeProgress, setIntakeProgress] = useState(null);
  const [assignedAttorney, setAssignedAttorney] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [fieldOverrides, setFieldOverrides] = useState([]);
  const [taskUsers, setTaskUsers] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'normal',
    due_date: ''
  });
  const [taskSaving, setTaskSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskError, setTaskError] = useState(null);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);

  useEffect(() => {
    const fetchMatter = async () => {
      if (!profile?.law_firm_id || !matterId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: matterData, error: matterErr } = await supabase
          .from('matters')
          .select(`
            *,
            client:clients (*)
          `)
          .eq('id', matterId)
          .eq('law_firm_id', profile.law_firm_id)
          .single();

        if (matterErr) throw matterErr;
        if (!matterData) throw new Error('Plan not found or access denied.');
        setMatter(matterData);

        const [{ data: progressData }, attorneyRes, usersRes, tasksRes, overridesRes] = await Promise.all([
          supabase
            .from('matter_intake_progress')
            .select('*')
            .eq('matter_id', matterId)
            .single(),
          matterData.assigned_attorney_id
            ? supabase
                .from('profiles')
                .select('id, first_name, last_name, email')
                .eq('id', matterData.assigned_attorney_id)
                .single()
            : Promise.resolve({ data: null }),
          supabase
            .from('profiles')
            .select('id, first_name, last_name, email, role, law_firm_id')
            .eq('law_firm_id', profile.law_firm_id)
            .in('role', ['attorney', 'legal_staff'])
            .order('last_name', { ascending: true }),
          supabase
            .from('matter_tasks')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: false }),
          supabase
            .from('matter_field_overrides')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: false })
        ]);

        setIntakeProgress(progressData || null);
        setAssignedAttorney(attorneyRes?.data || null);

        if (usersRes.error) {
          console.warn('Law firm task users error:', usersRes.error);
        } else {
          setTaskUsers(usersRes.data || []);
        }

        if (tasksRes.error) {
          console.warn('Matter tasks error:', tasksRes.error);
          setTaskError(formatSupabaseError(tasksRes.error, 'Unable to load tasks.'));
        } else {
          setTasks(tasksRes.data || []);
        }

        if (overridesRes.error) {
          console.warn('Matter field overrides error:', overridesRes.error);
        } else {
          setFieldOverrides(overridesRes.data || []);
        }
      } catch (err) {
        console.error('Law firm matter detail error:', err);
        setError(formatSupabaseError(err, 'Unable to load assigned plan.'));
      } finally {
        setLoading(false);
      }
    };

    fetchMatter();
  }, [matterId, profile]);

  const handleTaskFormChange = (e) => {
    const { name, value } = e.target;
    setTaskForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!matter || taskSaving) return;

    try {
      setTaskSaving(true);
      setTaskError(null);

      if (!taskForm.title.trim()) {
        throw new Error('Task title is required.');
      }

      const { data, error: insertErr } = await supabase
        .from('matter_tasks')
        .insert({
          matter_id: matter.id,
          law_firm_id: matter.law_firm_id,
          assigned_to: taskForm.assigned_to || null,
          created_by: profile.id,
          title: taskForm.title.trim(),
          description: taskForm.description.trim() || null,
          priority: taskForm.priority,
          due_date: taskForm.due_date || null
        })
        .select('*')
        .single();

      if (insertErr) throw insertErr;

      await supabase.from('audit_logs').insert({
        user_id: profile.id,
        action: 'matter_task_created',
        entity_type: 'matter_task',
        entity_id: data.id,
        metadata: {
          matter_id: matter.id,
          assigned_to: data.assigned_to,
          priority: data.priority,
          status: data.status
        }
      });

      setTasks((prev) => [data, ...prev]);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'normal',
        due_date: ''
      });
    } catch (err) {
      console.error('Create matter task error:', err);
      setTaskError(formatSupabaseError(err, 'Unable to create task.'));
    } finally {
      setTaskSaving(false);
    }
  };

  const handleTaskStatusChange = async (task, nextStatus) => {
    try {
      setTaskError(null);

      const { data, error: updateErr } = await supabase
        .from('matter_tasks')
        .update({
          status: nextStatus,
          completed_at: nextStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', task.id)
        .select('*')
        .single();

      if (updateErr) throw updateErr;

      await supabase.from('audit_logs').insert({
        user_id: profile.id,
        action: 'matter_task_status_updated',
        entity_type: 'matter_task',
        entity_id: task.id,
        metadata: {
          matter_id: task.matter_id,
          previous_status: task.status,
          status: nextStatus
        }
      });

      setTasks((prev) => prev.map((item) => (item.id === data.id ? data : item)));
    } catch (err) {
      console.error('Update matter task error:', err);
      setTaskError(formatSupabaseError(err, 'Unable to update task.'));
    }
  };

  const handleMatterStatusChange = async (nextStatus) => {
    if (!matter || nextStatus === matter.status) return;

    try {
      setStatusUpdating(true);
      setError(null);

      const previousStatus = matter.status;
      const { data, error: updateErr } = await supabase
        .from('matters')
        .update({ status: nextStatus })
        .eq('id', matter.id)
        .eq('law_firm_id', profile.law_firm_id)
        .select('*, client:clients (*)')
        .single();

      if (updateErr) throw updateErr;

      await supabase.from('audit_logs').insert({
        user_id: profile.id,
        action: 'matter_workflow_status_updated',
        entity_type: 'matter',
        entity_id: matter.id,
        metadata: {
          previous_status: previousStatus,
          status: nextStatus
        }
      });

      setMatter(data);
    } catch (err) {
      console.error('Matter workflow status update error:', err);
      setError(formatSupabaseError(err, 'Unable to update workflow status.'));
    } finally {
      setStatusUpdating(false);
    }
  };

  if (!profile?.law_firm_id) {
    return (
      <div className="p-8 text-center bg-white rounded-lg border border-slate-200">
        Law firm access requires a law firm profile assignment.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading assigned plan...</p>
      </div>
    );
  }

  if (error && !matter) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-8 bg-white rounded-xl shadow-sm border border-red-100 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Unable to load plan</h2>
        <p className="text-slate-600 mt-2">{error}</p>
        <button
          onClick={() => navigate('/law-firm/matters')}
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
        >
          Back to assigned plans
        </button>
      </div>
    );
  }

  const getTaskUserName = (userId) => {
    const user = taskUsers.find((item) => item.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unassigned';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/law-firm/matters')}
            className="mr-4 p-2 rounded-full hover:bg-white transition-colors text-slate-400 hover:text-slate-600"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                {matter.client?.first_name} {matter.client?.last_name}
              </h1>
              {getStatusBadge(matter.status)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {matter.plan_type} - Created {format(new Date(matter.created_at), 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/law-firm/matters/${matter.id}/review`)}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiClipboard} className="mr-2 h-4 w-4" />
          Attorney Review
        </button>
        <button
          onClick={() => navigate(`/law-firm/matters/${matter.id}/documents`)}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiFileText} className="mr-2 h-4 w-4" />
          Document Workspace
        </button>
        <button
          onClick={() => navigate('/law-firm/templates')}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiSettings} className="mr-2 h-4 w-4" />
          Manage Templates
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-tight">Plan Overview</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Plan Type</label>
                  <p className="text-sm text-slate-900 font-medium">{matter.plan_type || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">State of Governance</label>
                  <p className="text-sm text-slate-900 font-medium">{matter.client_state || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Complexity</label>
                  <p className="text-sm text-slate-900 font-medium capitalize">{matter.complexity_level}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Legal Fee Status</label>
                  <p className="text-sm text-slate-900 font-medium capitalize">{matter.legal_fee_status?.replaceAll('_', ' ')}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Intake Status</label>
                  <p className="text-sm text-slate-900 font-medium capitalize">
                    {intakeProgress?.status?.replaceAll('_', ' ') || 'Not started'}
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Assigned Attorney</label>
                  <p className="text-sm text-slate-900 font-medium">
                    {assignedAttorney ? `${assignedAttorney.first_name} ${assignedAttorney.last_name}` : 'Not assigned'}
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Advisor Notes</label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 text-sm text-slate-700 italic">
                  {matter.advisor_notes || 'No internal notes provided for this plan.'}
                </div>
              </div>
            </div>
          </div>

          <ProfessionalLearningInsights matter={matter} />

          <ProfessionalAssignLearning matter={matter} profile={profile} />

          {fieldOverrides.length > 0 && (
            <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-emerald-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="font-bold text-emerald-950 flex items-center text-sm uppercase tracking-tight">
                  <SafeIcon icon={FiIcons.FiEdit3} className="mr-2 h-4 w-4 text-emerald-700" />
                  Attorney-Approved Values
                </h3>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                  {fieldOverrides.length} Saved
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {fieldOverrides.map((override) => (
                  <div key={override.id} className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{override.field_key}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Original</p>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{stringifyValue(override.source_value_json)}</p>
                          </div>
                          <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Approved</p>
                            <p className="text-sm text-emerald-950 whitespace-pre-line">{stringifyValue(override.approved_value_json)}</p>
                          </div>
                        </div>
                      </div>
                      {override.approved_at && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                          {format(new Date(override.approved_at), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DocumentGenerationPanel
            matter={matter}
            onDocumentGenerated={() => {
              setMatter((current) => current ? { ...current, status: 'documents_generated' } : current);
              setDocumentsRefreshKey((key) => key + 1);
            }}
          />

          <MatterDocumentsPanel matter={matter} mode="law_firm" refreshKey={documentsRefreshKey} />

          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
                <SafeIcon icon={FiIcons.FiCheckSquare} className="mr-2 h-4 w-4 text-slate-400" />
                Legal Staff Tasks
              </h3>
            </div>

            <form onSubmit={handleCreateTask} className="p-6 border-b border-slate-100 bg-slate-50/40 space-y-4">
              {taskError && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
                  {taskError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
                  <input
                    name="title"
                    type="text"
                    required
                    value={taskForm.title}
                    onChange={handleTaskFormChange}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assign To</label>
                  <select
                    name="assigned_to"
                    value={taskForm.assigned_to}
                    onChange={handleTaskFormChange}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                  >
                    <option value="">Unassigned</option>
                    {taskUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.role.replace('_', ' ')})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  value={taskForm.description}
                  onChange={handleTaskFormChange}
                  className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select
                    name="priority"
                    value={taskForm.priority}
                    onChange={handleTaskFormChange}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                  <input
                    name="due_date"
                    type="date"
                    value={taskForm.due_date}
                    onChange={handleTaskFormChange}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={taskSaving}
                    className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50 transition-colors"
                  >
                    {taskSaving ? (
                      <>
                        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
                        Add Task
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {tasks.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {tasks.map((task) => (
                  <div key={task.id} className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-semibold text-slate-900">{task.title}</h4>
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          {task.priority}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                          {getTaskStatusLabel(task.status)}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-sm text-slate-600 mt-2">{task.description}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        Assigned to {getTaskUserName(task.assigned_to)}
                        {task.due_date && ` - Due ${format(new Date(`${task.due_date}T00:00:00`), 'MMM d, yyyy')}`}
                      </p>
                    </div>
                    <select
                      value={task.status}
                      onChange={(e) => handleTaskStatusChange(task, e.target.value)}
                      className="block w-full lg:w-44 sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="blocked">Blocked</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <SafeIcon icon={FiIcons.FiCheckSquare} className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm">No tasks have been created for this plan yet.</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center text-sm uppercase tracking-tight">
              <SafeIcon icon={FiIcons.FiGitBranch} className="mr-2 h-4 w-4 text-slate-400" /> Workflow Status
            </h3>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Current Stage</label>
            <select
              value={matter.status}
              onChange={(e) => handleMatterStatusChange(e.target.value)}
              disabled={statusUpdating}
              className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white disabled:bg-slate-50"
            >
              {WORKFLOW_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            {statusUpdating && (
              <p className="text-xs text-slate-500 mt-2">Updating workflow...</p>
            )}
          </div>

          <div className="bg-white shadow-sm rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center text-sm uppercase tracking-tight">
              <SafeIcon icon={FiIcons.FiUser} className="mr-2 h-4 w-4 text-slate-400" /> Client Profile
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiMail} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Email Address</p>
                  <p className="text-sm text-slate-900 break-all font-medium">{matter.client?.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiPhone} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Phone Number</p>
                  <p className="text-sm text-slate-900 font-medium">{matter.client?.phone || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiMapPin} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Residency</p>
                  <p className="text-sm text-slate-900 font-medium">{matter.client?.state_of_residence || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center text-sm uppercase tracking-tight">
              <SafeIcon icon={FiIcons.FiClipboard} className="mr-2 h-4 w-4 text-slate-400" /> Intake Progress
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Completion</p>
                <p className="text-2xl font-bold text-slate-900">{intakeProgress?.completion_percent || 0}%</p>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-700"
                  style={{ width: `${intakeProgress?.completion_percent || 0}%` }}
                />
              </div>
              {intakeProgress?.submitted_at && (
                <p className="text-xs text-slate-500">
                  Submitted {format(new Date(intakeProgress.submitted_at), 'MMM d, yyyy')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LawFirmMatterDetail;
